import { ensureSupabaseClientConfig } from '@/lib/env/runtime-env';

const { url: SUPABASE_URL } = ensureSupabaseClientConfig();

// Bridge API Configuration
export const BRIDGE_CONFIG = {
  // Developer fee rate (2%)
  DEVELOPER_FEE_RATE: 0.02,

  // Bridge API base URL for exchange rates (environment-aware)
  // Using new single-rate endpoint for calculator (replaces deprecated bridge-exchange-rates)
  EXCHANGE_RATES_ENDPOINT: `${SUPABASE_URL}/functions/v1/bridge-exchange-rate-single`,

  // Payment rail fees (based on Bridge API documentation)
  // These are typical fees - actual fees may vary by account
  RAIL_FEES: {
    ACH: {
      fixed: 0, // No fixed fee
      percent: 0, // 0% fee
      minFee: 0,
      maxFee: 0,
      eta: '1-3 business days',
    },
    SEPA: {
      fixed: 0, // No fixed fee
      percent: 0, // 0% fee
      minFee: 0,
      maxFee: 0,
      eta: '1-2 business days',
    },
    WIRE: {
      fixed: 25, // $25 flat fee typical for wire
      percent: 0,
      minFee: 25,
      maxFee: 25,
      eta: 'Same day',
    },
    SPEI: {
      fixed: 0,
      percent: 0.001, // 0.1%
      minFee: 0,
      maxFee: 50, // MXN
      eta: 'Same day',
    },
    PIX: {
      fixed: 0,
      percent: 0.001, // 0.1%
      minFee: 0,
      maxFee: 10, // BRL
      eta: 'Instant',
    },
  } as const,

  // Network fees for CRYPTO-TO-CRYPTO transfers only
  // 
  // ⚠️ IMPORTANT: Bridge API Fee Structure
  // Per Bridge docs (https://apidocs.bridge.xyz/platform/orchestration/fees-and-mins/devfees):
  // 
  // OFFRAMP (wallet → bank): Bridge ABSORBS network fees (gas_fee = $0)
  //   Formula: final_amount = initial_amount - developer_fee - exchange_fee
  //   Network fee NOT charged to user
  // 
  // CRYPTO-TO-CRYPTO (wallet → external address): User pays network fees
  //   Formula: final_amount = initial_amount - developer_fee - gas_fee
  //   Network fee varies by chain congestion
  //
  // These estimates are for crypto-to-crypto transfers only.
  // ⚠️ Actual fees vary significantly based on network congestion.
  // Last updated: 2025-12-04 (based on average network conditions)
  NETWORK_FEES: {
    ethereum: { estimated: 2.00, currency: 'USD', note: 'Varies $0.50-$20+ based on congestion' },
    polygon: { estimated: 0.01, currency: 'USD', note: 'Very cheap L2' },
    base: { estimated: 0.02, currency: 'USD', note: 'Cheap Coinbase L2' },
    arbitrum: { estimated: 0.05, currency: 'USD', note: 'Cheap L2' },
    optimism: { estimated: 0.03, currency: 'USD', note: 'Cheap L2' },
    avalanche: { estimated: 0.05, currency: 'USD', note: 'Fast L1' },
    solana: { estimated: 0.00025, currency: 'USD', note: 'Very cheap' },
    stellar: { estimated: 0.00001, currency: 'USD', note: 'Essentially free' },
    tron: { estimated: 0.50, currency: 'USD', note: 'Energy/bandwidth model' },
  } as const,

  // Transaction minimum rules (based on Bridge Route Explorer)
  // Source: https://apidocs.bridge.xyz/get-started/introduction/what-we-support/payment-routes
  // Last updated: 2025-12-03
  MINIMUM_RULES: {
    // Default minimums by type
    DEFAULT_FIAT: 1, // $1/€1 for fiat
    DEFAULT_STABLECOIN: 1, // 1 unit for stablecoins (USDC, USDB, PYUSD, EURC, USDP)
    DEFAULT_CRYPTO: 2, // 2 units for other crypto

    // Special asset minimums (Bridge-specific)
    DAI: 2,     // 2 DAI base minimum (20 on Ethereum)
    USDT: 2,    // 2 USDT base minimum (20 on Ethereum, 5 on Tron)
    CASH: 1,    // 1 CASH (Solana only)

    // Network-specific minimums (higher for expensive networks)
    NETWORK_MINIMUMS: {
      ethereum: {
        DAI: 20,   // 20 DAI minimum on Ethereum (gas costs)
        USDT: 20,  // 20 USDT minimum on Ethereum (gas costs)
        ETH: 20,   // 20 USD minimum for ETH purchases
        USDC: 1,
        PYUSD: 1,
      },
      tron: {
        USDT: 5, // 5 USDT minimum on Tron (from Bridge docs)
      },
      solana: {
        EURC: 2, // 2 EURC minimum to SEPA
      },
    },

    // Currency-specific minimums (fiat rails)
    CURRENCY_MINIMUMS: {
      MXN: {
        source: 50, // 50 MXN minimum when sending via SPEI
        destination: 2, // 2 units minimum when receiving MXN
      },
      BRL: {
        source: 10, // 10 BRL minimum via PIX (Bridge Requirement)
        destination: 2, // 2 units minimum when receiving BRL
      },
    },
  },

  // Withdrawal minimum amounts (from Bridge Route Explorer - accurate as of 2025-12-03)
  // Source: https://apidocs.bridge.xyz/get-started/introduction/what-we-support/payment-routes
  WITHDRAWAL_MINIMUMS: {
    // Fiat withdrawal minimums by payment rail
    fiat: {
      ACH: 1,    // $1 minimum for ACH (USD)
      WIRE: 1,   // $1 minimum for Wire (USD)
      SEPA: 1,   // €1 minimum for SEPA (EUR), 2 for some stablecoins
      SPEI: 2,   // 2 USDC/USDB minimum to MXN
      PIX: 2,    // 2 USDC/USDB minimum to BRL
    },
    // Crypto withdrawal minimums by asset
    crypto: {
      USDC: 1,   // 1 USDC minimum (most routes)
      USDB: 1,   // 1 USDB minimum
      PYUSD: 1,  // 1 PYUSD minimum
      EURC: 1,   // 1 EURC minimum (2 for some routes)
      DAI: 2,    // 2 DAI minimum (20 on Ethereum)
      USDT: 2,   // 2 USDT minimum (5 on Tron, 20 on Ethereum)
      USDP: 1,   // 1 USDP minimum
      CASH: 1,   // 1 CASH minimum (Solana only)
    },
    // Network-specific crypto withdrawal minimums
    network: {
      ethereum: 1, // Base minimum (DAI/USDT have higher asset-specific mins)
      polygon: 1,
      base: 1,
      arbitrum: 1,
      optimism: 1,
      avalanche: 1,
      solana: 1,
      stellar: 1,
      tron: 2,    // 2 for USDT (5 USDT from Tron)
    },
    // Legacy bank field (deprecated - use fiat instead)
    bank: {
      USD: 1,   // $1 minimum for USD via ACH/Wire
      EUR: 1,   // €1 minimum for EUR via SEPA
      MXN: 2,   // 2 USDC equivalent (~$2) for SPEI
      BRL: 2,   // 2 USDC equivalent (~$2) for PIX
    },
  } as const,

  // Exchange rate cache duration (2 minutes)
  EXCHANGE_RATE_CACHE_DURATION: 2 * 60 * 1000,
} as const;

// Supported assets for liquidation addresses (only stablecoins - ETH and BTC not supported)
export type BridgeAsset = 'USDC' | 'USDT' | 'PYUSD' | 'USDB' | 'EURC' | 'DAI' | 'USDP';
// Supported networks for liquidation addresses (bitcoin network not supported for address creation)
export type BridgeNetwork = 'ethereum' | 'polygon' | 'base' | 'arbitrum' | 'optimism' | 'avalanche' | 'solana' | 'stellar' | 'tron';
export type FiatCurrency = 'USD' | 'EUR' | 'MXN' | 'BRL';
export type PaymentRail = 'ACH' | 'SEPA' | 'WIRE' | 'SPEI' | 'PIX';

// Payment rail to currency mapping (SSOT from Bridge API)
export const RAIL_CURRENCY_MAPPING: Record<PaymentRail, FiatCurrency[]> = {
  ACH: ['USD'],
  SEPA: ['EUR'],
  WIRE: ['USD', 'EUR'],
  SPEI: ['MXN'],
  PIX: ['BRL'],
};

// Currency to available rails mapping
export const CURRENCY_RAILS_MAPPING: Record<FiatCurrency, PaymentRail[]> = {
  USD: ['ACH', 'WIRE'],
  EUR: ['SEPA', 'WIRE'],
  MXN: ['SPEI', 'WIRE'],
  BRL: ['PIX', 'WIRE'],
};